import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MULTI = ["#ef4444", "#fbbf24", "#38bdf8", "#4ade80", "#a78bfa", "#ec4899"];
const HEX = { red: "#ef4444", gold: "#fbbf24", blue: "#38bdf8", green: "#4ade80", purple: "#a78bfa" };

// 🎆 FIREWORKS SHOWS — merchant-bought bursts that reveal a name + deal card.
export const Fireworks = () => {
  const navigate = useNavigate();
  const [burst, setBurst] = useState(null);
  const [card, setCard] = useState(null);
  const showsRef = useRef([]);
  const busyRef = useRef(false);
  const timersRef = useRef(new Set());

  const after = useCallback((fn, ms) => {
    const t = setTimeout(() => { timersRef.current.delete(t); fn(); }, ms);
    timersRef.current.add(t);
  }, []);

  useEffect(() => () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current.clear();
  }, []);

  const launch = useCallback((show) => {
    if (busyRef.current) return;
    busyRef.current = true;
    const x = 12 + Math.random() * 70;
    const y = 16 + Math.random() * 28;
    setBurst({ id: `${show.id}-${Date.now()}`, x, y, show });
    after(() => {
      setCard({ x, y, show });
      after(() => setBurst(null), 1400);
      after(() => { setCard(null); busyRef.current = false; }, 7500);
    }, 950);
  }, [after]);

  useEffect(() => {
    let timers = [];
    axios.get(`${API}/fireworks/active`).then((r) => {
      showsRef.current = r.data.shows || [];
      showsRef.current.forEach((show, i) => {
        const every = Math.max(60, (show.interval_min || 6) * 60) * 1000;
        timers.push(setTimeout(() => {
          launch(show);
          timers.push(setInterval(() => launch(show), every));
        }, 9000 + i * 5000 + Math.random() * 8000));
      });
    }).catch(() => {});
    return () => timers.forEach((t) => { clearTimeout(t); clearInterval(t); });
  }, [launch]);

  const colors = (show) => (show.color === "multi" ? MULTI : [HEX[show.color] || "#fbbf24"]);

  return (
    <>
      <AnimatePresence>
        {burst && (
          <div key={burst.id} className="fixed inset-0 pointer-events-none z-[620]">
            <motion.div
              initial={{ left: `${burst.x}%`, top: "92%", opacity: 1 }}
              animate={{ top: `${burst.y}%` }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="absolute w-1 h-5 rounded-full"
              style={{ background: colors(burst.show)[0], boxShadow: `0 0 10px ${colors(burst.show)[0]}` }}
            />
            <div className="absolute" style={{ left: `${burst.x}%`, top: `${burst.y}%` }} data-testid="firework-burst">
              {Array.from({ length: 18 }).map((_, i) => {
                const ang = (i / 18) * Math.PI * 2;
                const dist = 55 * (burst.show.scale || 1);
                const col = colors(burst.show)[i % colors(burst.show).length];
                return (
                  <motion.span
                    key={i}
                    initial={{ x: 0, y: 0, opacity: 0 }}
                    animate={{ x: Math.cos(ang) * dist, y: Math.sin(ang) * dist, opacity: [0, 1, 1, 0] }}
                    transition={{ duration: 1.25, delay: 0.9, ease: "easeOut" }}
                    className="absolute rounded-full"
                    style={{ width: 6 * (burst.show.scale || 1), height: 6 * (burst.show.scale || 1), background: col, boxShadow: `0 0 12px ${col}` }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {card && (
          <motion.button
            data-testid="firework-reveal-card"
            initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }}
            onClick={() => navigate(`/d/${card.show.business_id}`)}
            className="fixed z-[1700] -translate-x-1/2 rounded-2xl px-4 py-2.5 text-center bg-slate-950/90 border-2 border-amber-400/70 shadow-[0_0_26px_rgba(251,191,36,0.45)] backdrop-blur"
            style={{ left: `${card.x}%`, top: `${card.y}%` }}
          >
            <p className="font-heading font-black text-sm text-amber-300 whitespace-nowrap">🎆 {card.show.business_name}</p>
            {card.show.deal_title && (
              <p className="text-[11px] font-bold text-white whitespace-nowrap">{card.show.deal_title}{card.show.deal_discount ? ` · ${card.show.deal_discount}` : ""}</p>
            )}
            <p className="text-[9px] font-black text-sky-300 uppercase tracking-widest mt-0.5">Tap for the deal →</p>
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};
